<template>
  <v-container fluid class="replenishment-page">
    <div class="page-header">
      <div>
        <div class="eyebrow">Reposición interna</div>
        <h1>{{ isEditMode ? "Editar solicitud" : "Nueva solicitud de reposición" }}</h1>
        <p>Armá un pedido por proveedor para que Compras lo revise y lo convierta en orden formal.</p>
      </div>
      <v-btn outlined color="primary" class="header-action" @click="goBack">
        <v-icon left>mdi-arrow-left</v-icon>
        Volver
      </v-btn>
    </div>

    <v-form ref="form" v-model="formValid" lazy-validation>
      <v-row>
        <v-col cols="12" lg="4">
          <v-card flat class="section-panel">
            <v-card-title>
              <v-icon left color="primary">mdi-truck-outline</v-icon>
              Proveedor y motivo
            </v-card-title>
            <v-card-text>
              <v-autocomplete
                v-model="order.providerId"
                :items="providers"
                item-text="name"
                item-value="id"
                label="Proveedor"
                outlined
                dense
                required
                :rules="[v => !!v || 'Seleccione un proveedor']"
                :loading="loadingProviders"
                :disabled="isEditMode || cartItems.length > 0"
                @change="onProviderChange"
              >
                <template v-slot:item="{ item }">
                  <v-list-item-content>
                    <v-list-item-title>{{ item.name }}</v-list-item-title>
                    <v-list-item-subtitle>{{ item.code }}</v-list-item-subtitle>
                  </v-list-item-content>
                </template>
              </v-autocomplete>

              <v-menu v-model="dateMenu" :close-on-content-click="false" max-width="290">
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    v-model="order.orderDate"
                    label="Fecha"
                    readonly
                    outlined
                    dense
                    required
                    :rules="[v => !!v || 'Seleccione una fecha']"
                    prepend-inner-icon="mdi-calendar"
                    v-bind="attrs"
                    v-on="on"
                  />
                </template>
                <v-date-picker v-model="order.orderDate" locale="es-AR" @input="dateMenu = false" />
              </v-menu>

              <v-textarea
                v-model="order.notes"
                label="Observaciones para Compras"
                outlined
                rows="4"
                placeholder="Urgencia, faltantes de góndola, obra, cliente, reposición crítica..."
              />
            </v-card-text>
          </v-card>

          <v-card flat class="section-panel mt-4 summary-panel">
            <v-card-title>
              <v-icon left color="primary">mdi-clipboard-list-outline</v-icon>
              Resumen
            </v-card-title>
            <v-card-text>
              <div class="summary-row">
                <span>Proveedor</span>
                <strong>{{ selectedProviderName }}</strong>
              </div>
              <div class="summary-row">
                <span>Artículos</span>
                <strong>{{ cartItems.length }}</strong>
              </div>
              <div class="summary-row">
                <span>Unidades solicitadas</span>
                <strong>{{ totalQuantity }}</strong>
              </div>
              <div class="summary-row summary-row--total">
                <span>Total neto estimado</span>
                <strong>{{ formatMoney(estimatedNetTotal) }}</strong>
              </div>
              <v-btn
                block
                color="primary"
                class="mt-4"
                :loading="saving"
                :disabled="!canSave"
                @click="saveOrder"
              >
                <v-icon left>mdi-send-check-outline</v-icon>
                {{ isEditMode ? "Guardar solicitud" : "Enviar a Compras" }}
              </v-btn>
            </v-card-text>
          </v-card>
        </v-col>

        <v-col cols="12" lg="8">
          <v-card flat class="section-panel">
            <v-card-title class="product-toolbar">
              <div>
                <v-icon left color="primary">mdi-magnify</v-icon>
                Artículos del proveedor
              </div>
              <v-chip small outlined color="primary">{{ cartItems.length }} seleccionados</v-chip>
            </v-card-title>
            <v-card-text>
              <v-alert v-if="!order.providerId" dense text type="info" class="mb-4">
                Seleccioná un proveedor para buscar únicamente sus artículos asociados.
              </v-alert>

              <v-text-field
                v-model="productSearchQuery"
                label="Buscar por código o descripción"
                outlined
                dense
                clearable
                prepend-inner-icon="mdi-magnify"
                :disabled="!order.providerId"
                :loading="searchingProducts"
                @input="debouncedSearchProducts"
              />

              <v-data-table
                :headers="productHeaders"
                :items="productResults"
                :loading="searchingProducts"
                :items-per-page="8"
                class="products-table"
                no-data-text="No hay artículos para mostrar"
                loading-text="Buscando artículos..."
              >
                <template v-slot:item.code="{ item }">
                  <span class="code-pill">{{ item.code }}</span>
                </template>
                <template v-slot:item.name="{ item }">
                  <div class="font-weight-medium">{{ item.name }}</div>
                  <div class="text-caption grey--text">{{ item.brand || item.shortDescription || "-" }}</div>
                </template>
                <template v-slot:item.standardCost="{ item }">
                  {{ formatMoney(item.standardCost) }}
                </template>
                <template v-slot:item.action="{ item }">
                  <v-btn small color="primary" :disabled="isInCart(item.id)" @click="selectProduct(item)">
                    <v-icon small left>{{ isInCart(item.id) ? "mdi-check" : "mdi-plus" }}</v-icon>
                    {{ isInCart(item.id) ? "Agregado" : "Agregar" }}
                  </v-btn>
                </template>
              </v-data-table>
            </v-card-text>
          </v-card>

          <v-card flat class="section-panel mt-4">
            <v-card-title>
              <v-icon left color="primary">mdi-cart-outline</v-icon>
              Solicitud
            </v-card-title>
            <v-card-text class="pa-0">
              <div v-if="cartItems.length === 0" class="empty-cart">
                <v-icon size="52" color="grey lighten-1">mdi-cart-plus</v-icon>
                <div>Agregá artículos para enviar la solicitud.</div>
              </div>
              <v-simple-table v-else class="request-table">
                <thead>
                  <tr>
                    <th>Código</th>
                    <th>Artículo</th>
                    <th class="text-center qty-col">Cantidad</th>
                    <th class="cost-col">Costo bruto</th>
                    <th class="discount-col">Bonif. %</th>
                    <th class="cost-col">Neto unit.</th>
                    <th class="cost-col">Total neto</th>
                    <th>Nota</th>
                    <th class="text-center action-col"></th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(item, index) in cartItems" :key="item.tempId || item.id">
                    <td><span class="code-pill">{{ item.product?.code || "-" }}</span></td>
                    <td>
                      <div class="font-weight-medium">{{ item.product?.name || item.product?.description || "-" }}</div>
                      <div class="text-caption grey--text">Costo de referencia: {{ formatMoney(item.product?.standardCost || 0) }}</div>
                    </td>
                    <td>
                      <v-text-field
                        v-model.number="item.grossUnitCost"
                        type="number"
                        min="0"
                        step="0.0001"
                        dense
                        outlined
                        hide-details
                        prefix="$"
                        @change="validateCommercialTerms(item)"
                      />
                    </td>
                    <td>
                      <v-text-field
                        v-model.number="item.discountPercent"
                        type="number"
                        min="0"
                        max="100"
                        step="0.01"
                        dense
                        outlined
                        hide-details
                        suffix="%"
                        class="centered-input"
                        @change="validateCommercialTerms(item)"
                      />
                    </td>
                    <td class="font-weight-medium">{{ formatMoney(netUnitCost(item)) }}</td>
                    <td class="font-weight-bold">{{ formatMoney(lineNetTotal(item)) }}</td>
                    <td>
                      <v-text-field
                        v-model.number="item.quantity"
                        type="number"
                        min="0.01"
                        step="0.01"
                        dense
                        outlined
                        hide-details
                        class="centered-input"
                        @change="validateQuantity(item)"
                      />
                    </td>
                    <td>
                      <v-text-field
                        v-model="item.notes"
                        dense
                        outlined
                        hide-details
                        placeholder="Uso interno, urgencia..."
                      />
                    </td>
                    <td class="text-center">
                      <v-btn icon small color="error" @click="removeItem(index)">
                        <v-icon small>mdi-delete-outline</v-icon>
                      </v-btn>
                    </td>
                  </tr>
                </tbody>
              </v-simple-table>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-form>

    <v-snackbar
      v-model="snackbar.show"
      :timeout="4000"
      top
      right
      :class="[`module-toast`, `module-toast--${snackbar.color}`]"
      content-class="white--text font-weight-bold"
    >
      {{ snackbar.message }}
      <template v-slot:action>
        <v-btn text color="white" @click="snackbar.show = false">Cerrar</v-btn>
      </template>
    </v-snackbar>
  </v-container>
</template>

<script>
import { createOrder, updateOrder, getOrder, generateIdempotencyKey } from "@/services/internalOrders";
import { currentAccountsService } from "@/services/currentAccounts";
import { productsService } from "@/services/products";
import { normalizeApiEnvelope } from "@/utils/apiEnvelope";
import { mapGetters } from "vuex";

export default {
  name: "InternalOrderCreate",
  data() {
    return {
      isEditMode: false,
      orderId: null,
      formValid: false,
      loadingProviders: false,
      saving: false,
      providers: [],
      dateMenu: false,
      order: {
        providerId: null,
        orderDate: new Date().toISOString().split("T")[0],
        notes: "",
      },
      cartItems: [],
      productSearchQuery: "",
      searchingProducts: false,
      productResults: [],
      productHeaders: [
        { text: "Código", value: "code", width: 120 },
        { text: "Artículo", value: "name" },
        { text: "Costo ref.", value: "standardCost", width: 120 },
        { text: "", value: "action", sortable: false, align: "end", width: 130 },
      ],
      searchTimeout: null,
      snackbar: { show: false, message: "", color: "success" },
    };
  },
  computed: {
    ...mapGetters("auth", ["user"]),
    selectedProviderName() {
      const provider = this.providers.find((x) => x.id === this.order.providerId);
      return provider ? provider.name : "Sin seleccionar";
    },
    totalQuantity() {
      return this.cartItems.reduce((sum, item) => sum + Number(item.quantity || 0), 0);
    },
    estimatedNetTotal() {
      return this.cartItems.reduce((sum, item) => sum + this.lineNetTotal(item), 0);
    },
    canSave() {
      return this.formValid && this.order.providerId && this.cartItems.length > 0;
    },
  },
  mounted() {
    this.loadProviders();
    if (this.$route.params.id) {
      this.isEditMode = true;
      this.orderId = this.$route.params.id;
      this.loadOrder();
    }
  },
  beforeDestroy() {
    clearTimeout(this.searchTimeout);
  },
  methods: {
    async loadProviders() {
      this.loadingProviders = true;
      try {
        const response = await currentAccountsService.list({ accountType: "supplier", limit: 1000 });
        const result = normalizeApiEnvelope(response);
        if (result.ok) {
          this.providers = result.data || [];
        } else {
          this.showError(result.error || "Error al cargar proveedores");
        }
      } catch (error) {
        console.error("Error loading providers:", error);
        this.showError("Error al cargar proveedores");
      } finally {
        this.loadingProviders = false;
      }
    },
    async loadOrder() {
      try {
        const response = await getOrder(this.orderId);
        if (!response.ok) {
          this.showError(response.error || "Error al cargar el pedido");
          this.goBack();
          return;
        }
        const order = response.data;
        this.order = {
          providerId: order.providerId,
          orderDate: order.orderDate,
          notes: order.notes || "",
        };
        this.cartItems = (order.items || []).map((item) => ({
          ...item,
          tempId: item.id,
          product: item.product,
          grossUnitCost: Number(item.grossUnitCost ?? item.product?.standardCost ?? 0),
          discountPercent: Number(item.discountPercent || 0),
        }));
      } catch (error) {
        console.error("Error loading order:", error);
        this.showError("Error al cargar el pedido");
        this.goBack();
      }
    },
    onProviderChange() {
      this.productResults = [];
      this.productSearchQuery = "";
    },
    debouncedSearchProducts() {
      clearTimeout(this.searchTimeout);
      this.searchTimeout = setTimeout(() => this.searchProducts(), 250);
    },
    async searchProducts() {
      if (!this.order.providerId) return;
      if (!this.productSearchQuery || this.productSearchQuery.length < 2) {
        this.productResults = [];
        return;
      }
      this.searchingProducts = true;
      try {
        const response = await productsService.list(this.productSearchQuery, {
          supplierId: this.order.providerId,
          status: 1,
          limit: 50,
        });
        const result = normalizeApiEnvelope(response);
        if (result.ok) {
          this.productResults = result.data || [];
        }
      } catch (error) {
        console.error("Error searching products:", error);
      } finally {
        this.searchingProducts = false;
      }
    },
    selectProduct(product) {
      if (this.isInCart(product.id)) return;
      this.cartItems.push({
        tempId: `temp_${Date.now()}_${product.id}`,
        productId: product.id,
        product,
        quantity: 1,
        grossUnitCost: Number(product.standardCost || 0),
        discountPercent: 0,
        notes: "",
      });
    },
    isInCart(productId) {
      return this.cartItems.some((item) => item.productId === productId);
    },
    removeItem(index) {
      this.cartItems.splice(index, 1);
    },
    validateQuantity(item) {
      if (!Number(item.quantity) || Number(item.quantity) <= 0) {
        item.quantity = 1;
        this.showError("La cantidad debe ser mayor a 0");
      }
    },
    validateCommercialTerms(item) {
      const grossUnitCost = Number(item.grossUnitCost);
      const discountPercent = Number(item.discountPercent);
      if (!Number.isFinite(grossUnitCost) || grossUnitCost < 0) {
        item.grossUnitCost = 0;
        this.showError("El costo bruto debe ser mayor o igual a 0");
      }
      if (!Number.isFinite(discountPercent) || discountPercent < 0 || discountPercent > 100) {
        item.discountPercent = 0;
        this.showError("La bonificación debe estar entre 0 y 100%");
      }
    },
    netUnitCost(item) {
      const grossUnitCost = Math.max(0, Number(item.grossUnitCost || 0));
      const discountPercent = Math.min(100, Math.max(0, Number(item.discountPercent || 0)));
      return grossUnitCost * (1 - discountPercent / 100);
    },
    lineNetTotal(item) {
      return Number(item.quantity || 0) * this.netUnitCost(item);
    },
    async saveOrder() {
      if (!this.$refs.form.validate() || this.cartItems.length === 0) return;
      this.saving = true;
      try {
        const orderData = {
          providerId: this.order.providerId,
          orderDate: this.order.orderDate,
          notes: this.order.notes,
          items: this.cartItems.map((item) => ({
            id: item.id,
            productId: item.productId,
            quantity: Number(item.quantity),
            grossUnitCost: Number(item.grossUnitCost || 0),
            discountPercent: Number(item.discountPercent || 0),
            notes: item.notes,
          })),
        };
        const key = generateIdempotencyKey(this.isEditMode ? "update" : "create");
        const response = this.isEditMode
          ? await updateOrder(this.orderId, orderData, key)
          : await createOrder(orderData, key);

        if (response.ok) {
          this.showSuccess(this.isEditMode ? "Solicitud actualizada" : "Solicitud enviada a Compras");
          this.goBack();
        } else {
          this.showError(response.error || "Error al guardar la solicitud");
        }
      } catch (error) {
        console.error("Error saving order:", error);
        this.showError(error.response?.data?.error || "Error al guardar la solicitud");
      } finally {
        this.saving = false;
      }
    },
    formatMoney(value) {
      return new Intl.NumberFormat("es-AR", { style: "currency", currency: "ARS" }).format(Number(value || 0));
    },
    goBack() {
      this.$router.push({ name: "internal-orders-list" });
    },
    showSuccess(message) {
      this.snackbar = { show: true, message, color: "success" };
    },
    showError(message) {
      this.snackbar = { show: true, message, color: "error" };
    },
  },
};
</script>

<style scoped>
.replenishment-page {
  --module-primary: var(--nav-link-active-color, #223b6a);
  --module-action: var(--accent-primary, #3b82f6);
  --module-line: var(--border-color, rgba(26, 35, 51, 0.1));
  --module-muted: var(--text-tertiary, #64748b);
  background: transparent;
  min-height: calc(100vh - 64px);
  padding-top: 12px;
}

.page-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 14px;
}

.page-header h1 {
  margin: 0;
  color: var(--module-primary);
  font-size: 22px;
  font-weight: 700;
  line-height: 1.25;
}

.page-header p {
  margin: 4px 0 0;
  color: var(--module-muted);
  font-size: 14px;
}

.eyebrow {
  color: var(--module-action);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: .04em;
  text-transform: uppercase;
}

.section-panel {
  border: 1px solid var(--module-line);
  border-radius: 8px;
  background: var(--bg-secondary, #fff) !important;
}

.section-panel .v-card__title {
  color: var(--text-primary, #1a2333);
  font-size: 14px;
  font-weight: 700;
  min-height: 48px;
  padding: 12px 16px;
  border-bottom: 1px solid var(--module-line);
}

.section-panel .v-card__text {
  padding: 14px 16px;
}

.section-panel >>> .v-label,
.section-panel >>> input,
.section-panel >>> textarea,
.section-panel >>> .v-select__selection,
.section-panel >>> .v-data-table {
  font-size: 14px;
}

.summary-row {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  padding: 8px 0;
  border-bottom: 1px solid var(--module-line);
  font-size: 13px;
}

.summary-row span {
  color: var(--module-muted);
}

.summary-row strong {
  color: var(--text-primary, #1a2333);
  text-align: right;
}

.summary-row--total strong {
  color: var(--module-primary);
  font-size: 15px;
}

.product-toolbar {
  display: flex;
  justify-content: space-between;
}

.code-pill {
  display: inline-flex;
  align-items: center;
  min-height: 24px;
  padding: 0 8px;
  border: 1px solid rgba(59, 130, 246, 0.28);
  border-radius: 6px;
  color: var(--module-primary);
  background: var(--accent-primary-light, rgba(59, 130, 246, 0.14));
  font-size: 11px;
  font-weight: 700;
}

.empty-cart {
  display: grid;
  place-items: center;
  gap: 8px;
  min-height: 132px;
  color: var(--module-muted);
  font-size: 14px;
}

.products-table >>> th,
.request-table th {
  color: var(--text-secondary, #475569) !important;
  font-size: 11px !important;
  font-weight: 700 !important;
  text-transform: uppercase;
}

.products-table >>> td,
.request-table td {
  font-size: 13px;
}

.qty-col {
  width: 112px;
}

.cost-col {
  min-width: 116px;
}

.discount-col {
  min-width: 104px;
}

.action-col {
  width: 56px;
}

.centered-input >>> input {
  text-align: center;
}

.module-toast .v-snack__wrapper {
  border-radius: 8px !important;
  box-shadow: 0 12px 28px rgba(15, 23, 42, 0.24) !important;
}

.module-toast--success .v-snack__wrapper {
  background: #166534 !important;
}

.module-toast--error .v-snack__wrapper {
  background: #991b1b !important;
}

@media (max-width: 720px) {
  .page-header {
    flex-direction: column;
  }

  .header-action {
    width: 100%;
  }
}
</style>
